/*
 * What the browser's tab says, composed in one place. Two things want a say in it — the screen that
 * is open and the number of things waiting in the Activity feed — and `document.title` is a single
 * string, so a second writer simply erases the first. Everything that contributes comes through
 * here instead: a screen names itself with `usePageTitle`, the unread indicator sets its count with
 * `setTabWaiting`, and this file builds the one string out of whatever is true at that moment.
 *
 * The name goes back to the plain product name when the screen that set it leaves. A tab that still
 * reads "Writing guidelines · teamBase" after the reader walked back into a channel says they are
 * somewhere they are not, and a person with a dozen tabs open picks by exactly that word.
 */

import { useEffect, useRef } from "react"
import { t } from "../i18n/index.ts"

/*
 * The screen that named the tab last. A route change can mount the next screen before the previous
 * one has run its cleanup, so a cleanup only clears the name it still owns; otherwise the arriving
 * screen's name would be wiped by the departing one.
 */
let owner: object | undefined
let named: string | undefined
let waiting = 0

/** Writes the tab's title from the two things that make it up. */
function paint(): void {
	const title =
		named === undefined || named === "" ? t("app.name") : t("shell.pageTitle", { title: named })
	globalThis.document.title = waiting > 0 ? t("shell.tabWaiting", { count: waiting, title }) : title
}

/**
 * Names the browser tab after what this screen has open, for as long as the screen is mounted. An
 * empty name is the same as none: the tab falls back to the product's name rather than showing a
 * stray separator while a screen is still finding out what it holds.
 */
export function usePageTitle(title: string): void {
	// One identity per mounted screen, which survives the title changing under it.
	const token = useRef({})
	useEffect(() => {
		const mine = token.current
		owner = mine
		named = title
		paint()
		return () => {
			if (owner !== mine) return
			owner = undefined
			named = undefined
			paint()
		}
	}, [title])
}

/**
 * How many things are waiting, drawn in front of the tab's name. The unread indicator
 * (`core/activity/indicator.ts`) is its only caller: it owns the count, this file owns the string.
 */
export function setTabWaiting(count: number): void {
	waiting = count
	paint()
}
